import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, FormControl, InputLabel, Select, MenuItem, Alert, Autocomplete
} from '@mui/material';
import { obligationsApi, contractsApi } from '../../services/api';

interface Props {
  open: boolean;
  defaultContractId?: string;
  onClose: () => void;
  onCreated: () => void;
}

const OBL_TYPES = ['sla', 'milestone', 'reporting', 'maintenance', 'escalation', 'performance', 'compliance', 'other'];
const RECURRENCE_TYPES = ['none', 'daily', 'weekly', 'monthly', 'quarterly', 'annually'];

export default function CreateObligationDialog({ open, defaultContractId, onClose, onCreated }: Props) {
  const [form, setForm] = useState({
    contract_id: defaultContractId || '',
    obligation_name: '',
    obligation_type: 'sla',
    description: '',
    status: 'pending',
    due_date: '',
    recurrence_type: 'none',
    sla_metric: '',
    sla_target_value: '',
    sla_unit: '',
    notes: ''
  });
  const [contracts, setContracts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      contractsApi.list({ status: 'active', limit: 100 }).then(res => setContracts(res.data.contracts));
      if (defaultContractId) setForm(f => ({ ...f, contract_id: defaultContractId }));
    }
  }, [open, defaultContractId]);

  const handleChange = (e: any) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload: any = { ...form };
      if (!payload.due_date) payload.due_date = null;
      if (!payload.sla_target_value) payload.sla_target_value = null;
      await obligationsApi.create(payload);
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create obligation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add Obligation</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Contract</InputLabel>
                <Select name="contract_id" value={form.contract_id} label="Contract" onChange={handleChange}>
                  {contracts.map(c => <MenuItem key={c.id} value={c.id}>{c.contract_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Obligation Name" name="obligation_name" value={form.obligation_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select name="obligation_type" value={form.obligation_type} label="Type" onChange={handleChange}>
                  {OBL_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Recurrence</InputLabel>
                <Select name="recurrence_type" value={form.recurrence_type} label="Recurrence" onChange={handleChange}>
                  {RECURRENCE_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth multiline rows={2} label="Description" name="description" value={form.description} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Due Date" name="due_date" type="date" value={form.due_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            {form.obligation_type === 'sla' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="SLA Metric" name="sla_metric" value={form.sla_metric} onChange={handleChange} placeholder="e.g. Uptime, Response Time" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="SLA Target" name="sla_target_value" type="number" value={form.sla_target_value} onChange={handleChange} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="SLA Unit" name="sla_unit" value={form.sla_unit} onChange={handleChange} placeholder="%, hours, days" />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" name="notes" value={form.notes} onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Creating...' : 'Create Obligation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
